import { SurgicalCaseEntity } from '@peritasai/repository';

const READY_TO_BUILD_STATUSES = new Set<string>([
  'Not Started',
  'In Progress',
  'Incomplete',
  'Trays Unavailable',
]);

const READY_FOR_DISPATCH_STATUSES = new Set<string>([
  'Ready for Dispatch',
  'Dispatched',
  'Dispatched - Incomplete',
]);

const DISPATCHED_STATUSES = new Set<string>([
  'Dispatched',
  'Dispatched - Incomplete',
]);

const ARRIVED_STATUSES = new Set<string>(['Arrived']);

// Whether a case within the readyForDispatch bucket has already been sent to
// OR (vs. still awaiting the Send to OR action) — used by the UI to pick the
// "Dispatched" vs. countdown card state.
export const isDispatchedCartStatus = (status: string): boolean =>
  DISPATCHED_STATUSES.has(status);

// Single source of truth for the card's stage — a case's queue bucket
// (readyToBuild/readyForDispatch/arrivedInOr) plus, within readyForDispatch,
// whether it has already been sent to OR. Import this rather than re-typing
// the stage strings at each call site.
export const CASE_CART_QUEUE_STAGE = {
  READY_TO_BUILD: 'readyToBuild',
  READY_FOR_DISPATCH: 'readyForDispatch',
  DISPATCHED: 'dispatched',
  ARRIVED_IN_OR: 'arrivedInOr',
} as const;

export type CaseCartAssemblyQueueStage =
  (typeof CASE_CART_QUEUE_STAGE)[keyof typeof CASE_CART_QUEUE_STAGE];

export type CaseQueues = {
  readyToBuild: SurgicalCaseEntity[];
  readyForDispatch: SurgicalCaseEntity[];
  arrivedInOr: SurgicalCaseEntity[];
};

const sortByDateTime = (cases: SurgicalCaseEntity[]): SurgicalCaseEntity[] =>
  [...cases].sort((a, b) => a.dateTime.getTime() - b.dateTime.getTime());

// No trays left to load/reserve — nothing pending, regardless of what the
// backend's cartCompletenessStatus string currently says.
const hasNoPendingItems = (c: SurgicalCaseEntity): boolean =>
  c.traysRequired > 0 && c.traysLoaded + c.traysReserved >= c.traysRequired;

export const splitIntoQueues = (cases: SurgicalCaseEntity[]): CaseQueues => {
  const readyToBuild: SurgicalCaseEntity[] = [];
  const readyForDispatch: SurgicalCaseEntity[] = [];
  const arrivedInOr: SurgicalCaseEntity[] = [];

  for (const c of cases) {
    // Arrival is a terminal state set explicitly by the OR's assign action —
    // unlike readyForDispatch, it is not gated on tray-pending checks. A case
    // can genuinely arrive even when its cart was never fully built (e.g. a
    // permanently short tray) — the OR's confirmation is the source of
    // truth, not a locally-recomputed tray count.
    if (ARRIVED_STATUSES.has(c.cartCompletenessStatus)) {
      arrivedInOr.push(c);
    } else if (
      READY_FOR_DISPATCH_STATUSES.has(c.cartCompletenessStatus) &&
      hasNoPendingItems(c)
    ) {
      readyForDispatch.push(c);
    } else if (READY_TO_BUILD_STATUSES.has(c.cartCompletenessStatus)) {
      readyToBuild.push(c);
    } else {
      // Unknown status: surface in the active queue so it is visible, not silently dropped
      readyToBuild.push(c);
    }
  }

  return {
    readyToBuild: sortByDateTime(readyToBuild),
    readyForDispatch: sortByDateTime(readyForDispatch),
    arrivedInOr: sortByDateTime(arrivedInOr),
  };
};
